import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, ArrowRight, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate("/app");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden border-r border-border">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(hsl(var(--primary)) 1px, transparent 1px)", backgroundSize: "26px 26px" }} />
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />

        <Link to="/" className="relative flex items-center gap-2.5 group" data-testid="login-home-link" aria-label="Back to home">
          <img src="/logo.png" alt="Arqitek" className="h-8 w-auto object-contain transition-opacity group-hover:opacity-80" />
        </Link>

        <div className="relative max-w-md">
          <div className="text-xs uppercase tracking-[0.25em] text-primary mb-4">ARQITEK · Real Estate Development Platform</div>
          <h1 className="font-display text-5xl font-bold tracking-tight leading-[1.05]">
            From Opportunity.<br /><span className="text-primary text-teal-glow">To Development.</span>
          </h1>
          <p className="mt-5 text-muted-foreground text-base leading-relaxed">
            ARQITEK brings property intelligence, acquisition, financial analysis, design, and development management into one connected platform—supporting every project from initial discovery through completion.
          </p>
        </div>

        <div className="relative text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Discover → Analyze → Acquire → Design → Develop → Deliver
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10" data-testid="login-home-link-mobile" aria-label="Back to home">
            <img src="/logo.png" alt="Arqitek" className="h-8 w-auto object-contain" />
          </Link>
          <h2 className="font-display text-3xl font-bold tracking-tight">Welcome back</h2>
          <p className="text-sm text-muted-foreground mt-1.5 mb-8">Sign in to your ARQITEK development command center.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="pr-11" />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full group" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Sign in
              {!loading && <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
